import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  HardDrive,
  Server,
  RotateCcw,
  SlidersHorizontal,
  ScrollText,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  Moon,
} from "lucide-react";

import { AetherMark } from "@/components/brand/AetherMark";
import { useTheme } from "@/hooks/useTheme";
import { ACCENTS, useAccent } from "@/hooks/useAccent";
import { cn } from "@/lib/utils";

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed(): void;
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
}

const SECTIONS: { label: string; items: NavItem[] }[] = [
  {
    label: "Fleet",
    items: [
      { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/targets", label: "Targets", icon: HardDrive },
      { to: "/machines", label: "Machines", icon: Server },
      { to: "/restore", label: "Restore", icon: RotateCcw },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/settings/retention", label: "Retention", icon: SlidersHorizontal },
      { to: "/settings/audit", label: "Audit log", icon: ScrollText },
    ],
  },
];

export function Sidebar({ collapsed, onToggleCollapsed }: SidebarProps) {
  const { theme, cycle } = useTheme();
  const { accent, setAccent } = useAccent();

  const nextAccent = () => {
    const i = ACCENTS.indexOf(accent);
    setAccent(ACCENTS[(i + 1) % ACCENTS.length]);
  };

  return (
    <aside className="sidebar">
      <div className="flex items-center gap-2 px-3 py-3">
        <AetherMark size={22} />
        {!collapsed && (
          <span className="text-md font-semibold tracking-[-0.01em]">Terrapi Aether</span>
        )}
        <button
          type="button"
          className="btn ghost icon sm ml-auto"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto">
        {SECTIONS.map((section) => (
          <div className="side-section" key={section.label}>
            {!collapsed && <div className="lbl">{section.label}</div>}
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "side-item",
                    isActive && "active",
                    // Keyboard focus indicator (WCAG 2.4.7) — the ds class only
                    // tints on hover/active, leaving keyboard nav invisible.
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
                  )
                }
                title={collapsed ? item.label : undefined}
              >
                <item.icon className="ic" />
                {!collapsed && <span className="lbl">{item.label}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="flex items-center gap-1 border-t border-border px-2 py-2">
        <button type="button" className="btn ghost icon sm" onClick={cycle} aria-label="Toggle theme">
          {theme === "dark" ? <Moon /> : <Sun />}
        </button>
        <button
          type="button"
          className="btn ghost sm"
          onClick={nextAccent}
          aria-label="Cycle accent"
        >
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-accent" />
          {!collapsed && <span className="capitalize">{accent}</span>}
        </button>
      </div>
    </aside>
  );
}
